import * as React from "react"
import { cn } from "@/lib/utils"
import { branding } from "@/lib/branding.config"

interface BrandMarkProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "default" | "lg"
  showText?: boolean
}

const sizeConfig = {
  sm: {
    container: "h-7 w-7 rounded-lg",
    initials: "text-[10px]",
    text: "text-sm",
  },
  default: {
    container: "h-9 w-9 rounded-xl",
    initials: "text-xs",
    text: "text-base",
  },
  lg: {
    container: "h-12 w-12 rounded-xl",
    initials: "text-sm",
    text: "text-lg",
  },
}

// Initials from the brand name: "AcmeGames" -> "AG", "vortex" -> "VO".
// Falls back to the first two letters whenever the capitals alone don't give
// two — "Bolkeys" has one capital and would otherwise render a lone "B".
const capitals = (branding.name.match(/[A-Z0-9]/g) || []).slice(0, 2).join("")
const initials =
  capitals.length === 2
    ? capitals
    : branding.name.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase()

function BrandMark({
  size = "default",
  showText = true,
  className,
  ...props
}: BrandMarkProps) {
  const config = sizeConfig[size]

  return (
    <div className={cn("flex items-center gap-2.5", className)} {...props}>
      <div
        className={cn(
          config.container,
          "flex items-center justify-center bg-gradient-to-br from-primary to-primary/70 shadow-lg shadow-primary/25 transition-all group-hover:shadow-primary/40"
        )}
      >
        <span className={cn("font-bold text-primary-foreground", config.initials)}>
          {initials}
        </span>
      </div>

      {showText && (
        <span className={cn("font-heading font-semibold text-foreground", config.text)}>
          {branding.name}
        </span>
      )}
    </div>
  )
}

export { BrandMark }
